/**
 * AI Safety Chatbot Component
 * Conversational AI assistant with voice support
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Bot, User, Send, Mic, MicOff, Plus, Trash2, MessageSquare,
  Loader2, Sparkles, Volume2, VolumeX, ChevronLeft, X,
  AlertTriangle, FileText, Shield, GraduationCap, Plane,
  HelpCircle, Clock, RefreshCw, ExternalLink, ClipboardList,
  BarChart3, Users, BookOpen, Settings, Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

// Navigation action links - maps keywords to pages
const ACTION_LINKS = [
  { 
    keywords: ['report incident', 'report an incident', 'new incident', 'submit incident', 'create incident', 'incident report'],
    label: 'Report Incident',
    page: 'incidents',
    icon: AlertTriangle,
    action: 'new'
  },
  { 
    keywords: ['view incident', 'recent incident', 'incident list', 'all incidents', 'incidents'],
    label: 'View Incidents',
    page: 'incidents',
    icon: FileText
  },
  { 
    keywords: ['risk assessment', 'risk register', 'assess risk', 'risk matrix'],
    label: 'Risk Register',
    page: 'risks',
    icon: Shield
  },
  { 
    keywords: ['frms', 'fatigue', 'crew fatigue', 'fatigue management', 'duty time'],
    label: 'FRMS Dashboard',
    page: 'frms',
    icon: Activity
  },
  { 
    keywords: ['training', 'training module', 'course', 'learn', 'certificate'],
    label: 'Training Modules',
    page: 'training-modules',
    icon: GraduationCap
  },
  { 
    keywords: ['audit', 'audit schedule', 'audit finding', 'internal audit'],
    label: 'Audits',
    page: 'audits',
    icon: ClipboardList
  },
  { 
    keywords: ['capa', 'corrective action', 'finding', 'corrective'],
    label: 'Findings & CAPA',
    page: 'capas',
    icon: Shield
  },
  { 
    keywords: ['hazard', 'report hazard', 'hazard report', 'safety hazard'],
    label: 'Report Hazard',
    page: 'hazards',
    icon: AlertTriangle
  },
  { 
    keywords: ['workflow', 'workflow builder', 'automation', 'process'],
    label: 'Workflow Builder',
    page: 'workflow-builder',
    icon: Settings
  },
  { 
    keywords: ['dashboard', 'overview', 'summary', 'home'],
    label: 'Dashboard',
    page: 'dashboard',
    icon: BarChart3
  },
  { 
    keywords: ['user', 'user management', 'permissions', 'access'],
    label: 'User Management',
    page: 'enterprise-users',
    icon: Users
  },
  { 
    keywords: ['help', 'guide', 'documentation', 'setup'],
    label: 'Help Center',
    page: 'help',
    icon: HelpCircle
  },
];

// Function to detect relevant action links from message content
const detectActionLinks = (content) => {
  if (!content) return [];
  const lowerContent = content.toLowerCase();
  const detectedLinks = [];
  
  for (const link of ACTION_LINKS) {
    for (const keyword of link.keywords) {
      if (lowerContent.includes(keyword)) {
        // Avoid duplicates
        if (!detectedLinks.find(l => l.page === link.page)) {
          detectedLinks.push(link);
        }
        break;
      }
    }
  }
  
  return detectedLinks.slice(0, 3); // Max 3 links per message
};

// Quick action suggestions
const QUICK_ACTIONS = [
  { icon: AlertTriangle, label: 'Report an incident', prompt: 'Help me report a new safety incident' },
  { icon: FileText, label: 'View recent incidents', prompt: 'Show me the recent incidents in the system' },
  { icon: Shield, label: 'Risk assessment help', prompt: 'Guide me through a risk assessment' },
  { icon: Plane, label: 'FRMS query', prompt: 'Show me the current crew fatigue status' },
  { icon: GraduationCap, label: 'Training info', prompt: 'What training modules are available?' },
  { icon: HelpCircle, label: 'SMS guidance', prompt: 'Explain the key components of an SMS' },
];

export default function AIChatbot({ isOpen, onClose, isFullPage = false, onNavigate }) {
  const { token, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Handle navigation to a page
  const handleNavigate = (page, action) => {
    if (onNavigate) {
      onNavigate(page, action);
    } else {
      // Fallback: use window location if no onNavigate prop
      const url = action ? `/?page=${page}&action=${action}` : `/?page=${page}`;
      window.location.href = url;
    }
  };

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-chat/sessions`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  }, [token]);

  // Load session messages
  const loadSession = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/ai-chat/sessions/${sessionId}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setCurrentSession({ id: data.id, title: data.title });
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  // Create new session
  const createNewSession = async () => {
    setCurrentSession(null);
    setMessages([]);
    setInputValue('');
  };

  // Delete session
  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/ai-chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Conversation deleted');
        if (currentSession?.id === sessionId) {
          createNewSession();
        }
        fetchSessions();
      }
    } catch (err) {
      toast.error('Failed to delete conversation');
    }
  };

  // Send message
  const sendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/ai-chat/chat`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          session_id: currentSession?.id,
          message: messageText.trim()
        })
      });

      if (res.ok) {
        const data = await res.json();
        
        // Update session info
        if (!currentSession) {
          setCurrentSession({ id: data.session_id, title: data.title });
        }
        
        // Add assistant message
        setMessages(prev => [...prev, data.message]);
        
        // Refresh sessions list
        fetchSessions();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Failed to get response');
      // Add error message
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording... Click again to stop');
    } catch (err) {
      console.error('Recording error:', err);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Transcribe audio
  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', 'en');

      const res = await fetch(`${API_URL}/api/ai-chat/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        if (data.text) {
          setInputValue(data.text);
          toast.success('Transcription complete');
        } else {
          toast.info('No speech detected in the recording');
        }
      } else {
        const errorMsg = data.error || data.detail || 'Transcription failed';
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error(`Failed to transcribe audio: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Initial load
  useEffect(() => {
    if (isOpen || isFullPage) {
      fetchSessions();
    }
  }, [isOpen, isFullPage, fetchSessions]);

  // Format timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen && !isFullPage) return null;

  const chatContent = (
    <div className={cn(
      "flex h-full",
      isFullPage ? "bg-slate-950" : ""
    )}>
      {/* Sidebar - Sessions List */}
      {showSidebar && (
        <div className={cn(
          "border-r border-slate-700 flex flex-col",
          isFullPage ? "w-72" : "w-64"
        )}>
          <div className="p-3 border-b border-slate-700">
            <Button
              onClick={createNewSession}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Conversation
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => loadSession(session.id)}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded-lg cursor-pointer group transition-colors",
                    currentSession?.id === session.id
                      ? "bg-indigo-600/20 border border-indigo-500/50"
                      : "hover:bg-slate-800"
                  )}
                >
                  <MessageSquare className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-slate-300 truncate">
                    {session.title || 'New Conversation'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => deleteSession(session.id, e)}
                    className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 hover:bg-red-900/30"
                  >
                    <Trash2 className="h-3 w-3 text-red-400" />
                  </Button>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-center text-slate-500 text-sm py-4">
                  No conversations yet
                </p>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="p-3 border-b border-slate-700 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSidebar(!showSidebar)}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", !showSidebar && "rotate-180")} />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">AeroGuard AI</h3>
              <p className="text-xs text-slate-400">Safety Assistant</p>
            </div>
          </div>
          <Badge className="ml-auto bg-emerald-600/20 text-emerald-400 border-emerald-600/50">
            <Sparkles className="h-3 w-3 mr-1" />
            GPT-4o
          </Badge>
          {!isFullPage && (
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4">
                <Bot className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">How can I help you today?</h3>
              <p className="text-slate-400 text-sm mb-6 max-w-md">
                I'm your AI safety assistant. Ask me about incidents, risk assessments, 
                FRMS, compliance, or any SMS-related questions.
              </p>
              
              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2 max-w-lg">
                {QUICK_ACTIONS.map((action, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4 bg-slate-800/50 border-slate-700 hover:bg-slate-700 hover:border-indigo-500"
                    onClick={() => sendMessage(action.prompt)}
                  >
                    <action.icon className="h-4 w-4 mr-2 text-indigo-400 flex-shrink-0" />
                    <span className="text-sm text-slate-300">{action.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => {
                const actionLinks = message.role === 'assistant' ? detectActionLinks(message.content) : [];
                
                return (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-3",
                      message.role === 'user' ? "justify-end" : "justify-start"
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <div className="max-w-[80%]">
                      <div
                        className={cn(
                          "rounded-2xl px-4 py-3",
                          message.role === 'user'
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-800 text-slate-200"
                        )}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <p className={cn(
                          "text-xs mt-1",
                          message.role === 'user' ? "text-indigo-200" : "text-slate-500"
                        )}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                      
                      {/* Action Links for assistant messages */}
                      {actionLinks.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {actionLinks.map((link, idx) => {
                            const IconComponent = link.icon;
                            return (
                              <Button
                                key={idx}
                                variant="outline"
                                size="sm"
                                onClick={() => handleNavigate(link.page, link.action)}
                                className="h-8 px-3 bg-slate-800/50 border-indigo-500/50 hover:bg-indigo-600/20 hover:border-indigo-400 text-indigo-300 hover:text-indigo-200 transition-colors"
                                data-testid={`action-link-${link.page}`}
                              >
                                <IconComponent className="h-3.5 w-3.5 mr-1.5" />
                                <span className="text-xs">{link.label}</span>
                                <ExternalLink className="h-3 w-3 ml-1.5 opacity-60" />
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-slate-300" />
                      </div>
                    )}
                  </div>
                );
              })}
              
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-slate-800 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
                      <span className="text-sm text-slate-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        <div className="p-3 border-t border-slate-700">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggleRecording}
              className={cn(
                "border-slate-700 flex-shrink-0",
                isRecording && "bg-red-600 border-red-600 hover:bg-red-700"
              )}
              disabled={isLoading}
            >
              {isRecording ? (
                <MicOff className="h-4 w-4 text-white" />
              ) : (
                <Mic className="h-4 w-4 text-slate-400" />
              )}
            </Button>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about safety management..."
              className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              disabled={isLoading}
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading}
              className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Press Enter to send • Click 🎤 for voice input
          </p>
        </div>
      </div>
    </div>
  );

  if (isFullPage) {
    return (
      <div className="h-[calc(100vh-120px)] bg-slate-900 rounded-lg border border-slate-700 overflow-hidden" data-testid="ai-chatbot-page">
        {chatContent}
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-[500px] h-[600px] bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden z-50" data-testid="ai-chatbot-popup">
      {chatContent}
    </div>
  );
}
